import { useState } from "react";
import type { FormEvent } from "react";
import { adminApi } from "../../api/adminApi";
import { useAuth } from "../../auth/AuthContext";
import { useDeployment } from "../../deployment/DeploymentContext";

/** The signed-in person's own account: who they are, and changing their password. */
export default function AccountPage() {
  const { user } = useAuth();
  const { status } = useDeployment();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (next.length < 8) return setError("The new password must be at least 8 characters.");
    if (next !== confirm) return setError("The two new passwords don't match.");
    setSaving(true);
    try {
      await adminApi.account.changePassword(current, next);
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not change the password");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>Account</h1>
      </div>

      <div className="page-panel">
        <h3>You</h3>
        <div className="form-grid">
          <div className="form-row">
            <label>Email</label>
            <input value={user?.email ?? ""} readOnly disabled />
          </div>
          <div className="form-row">
            <label>Role</label>
            <input value={(user?.roles ?? []).join(", ")} readOnly disabled />
          </div>
          {status?.organisationName && (
            <div className="form-row">
              <label>Organisation</label>
              <input value={status.organisationName} readOnly disabled />
            </div>
          )}
        </div>
      </div>

      <form className="page-panel" onSubmit={handleSubmit}>
        <h3>Change your password</h3>
        {error && <div className="error-text">{error}</div>}
        {done && <div className="license-notice">Password changed. Use the new one next time you sign in.</div>}
        <div className="form-grid">
          <div className="form-row">
            <label htmlFor="cur">Current password</label>
            <input id="cur" type="password" required autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div className="form-row">
            <label htmlFor="new">New password</label>
            <input id="new" type="password" required minLength={8} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
            <span className="help-text">At least 8 characters.</span>
          </div>
          <div className="form-row">
            <label htmlFor="conf">Confirm new password</label>
            <input id="conf" type="password" required minLength={8} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
        </div>
        <button type="submit" className="btn-primary" disabled={saving || !current || !next}>
          {saving ? "Saving..." : "Change password"}
        </button>
      </form>
    </div>
  );
}
